import { ApiErrorCode } from '@core/types/api.types.ts';
import type { ApiError } from '@core/types/api.types.ts';
import type { AxiosError } from 'axios';

interface ApiErrorResponseData {
  message?: string;
  error?: string;
  errors?: unknown;
  suggestion?: {
    recommendedChunkMode: string;
    estimatedChunks: number;
    pageSize: number;
  };
}

export class AppApiError extends Error implements ApiError {
  public readonly code: ApiErrorCode;
  public readonly status?: number;
  public readonly details?: Record<string, unknown>;
  public readonly originalError?: Error;
  public readonly retryable: boolean;
  public readonly timestamp: string;

  constructor(error: ApiError) {
    super(error.message);
    this.name = 'AppApiError';
    this.code = error.code;
    this.status = error.status;
    this.details = error.details;
    this.originalError = error.originalError;
    this.retryable = error.retryable;
    this.timestamp = error.timestamp || new Date().toISOString();
  }

  static fromAxiosError(error: AxiosError): AppApiError {
    if (error.code === 'ECONNABORTED') {
      return new AppApiError({
        code: ApiErrorCode.TIMEOUT,
        message: 'La solicitud excedió el tiempo de espera',
        retryable: true,
        timestamp: new Date().toISOString(),
        originalError: error,
      });
    }

    if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
      return new AppApiError({
        code: ApiErrorCode.ABORTED,
        message: 'La solicitud fue cancelada',
        retryable: false,
        timestamp: new Date().toISOString(),
        originalError: error,
      });
    }

    if (!error.response) {
      return new AppApiError({
        code: ApiErrorCode.NETWORK_ERROR,
        message: 'No se pudo conectar con el servidor',
        retryable: true,
        timestamp: new Date().toISOString(),
        originalError: error,
      });
    }

    const status = error.response.status;
    const responseData = error.response.data as ApiErrorResponseData | undefined;
    const serverMessage = responseData?.message || error.message;

    const statusMap: Record<number, { code: ApiErrorCode; retryable: boolean }> = {
      400: { code: ApiErrorCode.BAD_REQUEST, retryable: false },
      401: { code: ApiErrorCode.UNAUTHORIZED, retryable: false },
      403: { code: ApiErrorCode.FORBIDDEN, retryable: false },
      404: { code: ApiErrorCode.NOT_FOUND, retryable: false },
      409: { code: ApiErrorCode.CONFLICT, retryable: false },
      422: { code: ApiErrorCode.VALIDATION_ERROR, retryable: false },
      429: { code: ApiErrorCode.RATE_LIMITED, retryable: true },
      500: { code: ApiErrorCode.SERVER_ERROR, retryable: true },
      502: { code: ApiErrorCode.SERVER_ERROR, retryable: true },
      503: { code: ApiErrorCode.SERVICE_UNAVAILABLE, retryable: true },
      504: { code: ApiErrorCode.TIMEOUT, retryable: true },
    };

    const mapped = statusMap[status] || { code: ApiErrorCode.UNKNOWN, retryable: false };

    const details: Record<string, unknown> = {};
    if (responseData?.errors) details.errors = responseData.errors;
    if (responseData?.suggestion) details.suggestion = responseData.suggestion;

    return new AppApiError({
      code: mapped.code,
      message: responseData?.error || serverMessage,
      status,
      details: Object.keys(details).length > 0 ? details : undefined,
      retryable: mapped.retryable,
      timestamp: new Date().toISOString(),
      originalError: error,
    });
  }

  get suggestion(): { recommendedChunkMode: string; estimatedChunks: number; pageSize: number } | undefined {
    return this.details?.suggestion as { recommendedChunkMode: string; estimatedChunks: number; pageSize: number } | undefined;
  }
}
